/**
 * Territories (piece 20; decisions 0227, 0250): effective-dated, overlapping
 * with an explicit precedence, and never an auto-assigning rule. 🔨 Territory
 * suggests, assignment decides: resolution reads a hand placement first and
 * the criteria after, a named-account territory outranks a geographic one,
 * the highest priority wins, and nothing here writes an assignment.
 */
import { sameCounty } from '../tax/localTax';

export const TERRITORY_CRITERION_KINDS = ['State', 'County', 'PostalRange', 'Radius', 'AccountList'] as const;
export type TerritoryCriterionKind = (typeof TERRITORY_CRITERION_KINDS)[number];

export interface CriterionLike {
  kind: TerritoryCriterionKind;
  stateCode?: string | null;
  county?: string | null;
  postalFrom?: string | null;
  postalTo?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radiusMiles?: number | null;
  companyId?: string | null;
}

export interface TerritoryLike {
  id: string;
  priority: number;
  startDate: Date | string;
  endDate?: Date | string | null;
  criteria: readonly CriterionLike[];
}

export interface PlaceLike {
  companyId: string;
  stateCode?: string | null;
  county?: string | null;
  postalCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export function criterionProblem(c: CriterionLike): string | null {
  switch (c.kind) {
    case 'State':
      return c.stateCode?.trim() ? null : 'a State criterion names the state';
    case 'County':
      return c.stateCode?.trim() && c.county?.trim() ? null : 'a County criterion names the state and the county';
    case 'PostalRange':
      return c.postalFrom?.trim() && c.postalTo?.trim() ? null : 'a PostalRange criterion names both ends';
    case 'Radius':
      return typeof c.latitude === 'number' && typeof c.longitude === 'number' && typeof c.radiusMiles === 'number' && c.radiusMiles > 0 ? null : 'a Radius criterion names a point and a distance above zero';
    case 'AccountList':
      return c.companyId ? null : 'an AccountList criterion names the Company';
    default:
      return `${String(c.kind)} is not a criterion kind`;
  }
}

/** Great-circle distance in miles, the arithmetic a radius criterion reads. */
export function milesBetween(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 3958.8 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

const postalKey = (code: string) => code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);

export function criterionMatches(c: CriterionLike, place: PlaceLike): boolean {
  switch (c.kind) {
    case 'State':
      return !!place.stateCode && place.stateCode.trim().toUpperCase() === (c.stateCode ?? '').trim().toUpperCase();
    case 'County':
      // 🔒 A Location's county is its official name now, so a criterion typed without County, Parish or Borough still matches it (decision 0737).
      return !!place.stateCode && !!place.county && place.stateCode.trim().toUpperCase() === (c.stateCode ?? '').trim().toUpperCase() && sameCounty(place.county, c.county);
    case 'PostalRange': {
      if (!place.postalCode) return false;
      const code = postalKey(place.postalCode);
      return code >= postalKey(c.postalFrom ?? '') && code <= postalKey(c.postalTo ?? '');
    }
    case 'Radius':
      if (typeof place.latitude !== 'number' || typeof place.longitude !== 'number' || typeof c.latitude !== 'number' || typeof c.longitude !== 'number') return false;
      return milesBetween({ latitude: c.latitude, longitude: c.longitude }, { latitude: place.latitude, longitude: place.longitude }) <= (c.radiusMiles ?? 0);
    case 'AccountList':
      return c.companyId === place.companyId;
    default:
      return false;
  }
}

export function territoryActiveOn(t: Pick<TerritoryLike, 'startDate' | 'endDate'>, onDate: Date): boolean {
  const start = new Date(t.startDate).getTime();
  const end = t.endDate ? new Date(t.endDate).getTime() : null;
  return start <= onDate.getTime() && (end === null || end >= onDate.getTime());
}

/**
 * The territory a place falls in: a named-account match outranks every
 * geographic one, then the highest priority wins; an effective-dated
 * boundary is read as at the date, so a redraw never moves last year.
 */
export function resolveTerritory(place: PlaceLike, territories: readonly TerritoryLike[], onDate = new Date()): TerritoryLike | null {
  const matches = territories
    .filter((t) => territoryActiveOn(t, onDate))
    .map((t) => ({ t, named: t.criteria.some((c) => c.kind === 'AccountList' && criterionMatches(c, place)), geographic: t.criteria.some((c) => c.kind !== 'AccountList' && criterionMatches(c, place)) }))
    .filter((m) => m.named || m.geographic)
    .sort((a, b) => Number(b.named) - Number(a.named) || b.t.priority - a.t.priority);
  return matches[0]?.t ?? null;
}

/** A hand placement wins over the criteria (decision 0227); a placed account never moves when the criteria change. */
export function territoryFor(placementId: string | null | undefined, places: readonly PlaceLike[], territories: readonly TerritoryLike[], onDate = new Date()): { territoryId: string | null; placed: boolean } {
  if (placementId) return { territoryId: placementId, placed: true };
  for (const place of places) {
    const found = resolveTerritory(place, territories, onDate);
    if (found) return { territoryId: found.id, placed: false };
  }
  return { territoryId: null, placed: false };
}

export interface CoverageInput {
  companies: readonly { id: string; name: string; territoryId: string | null; placed: boolean }[];
  territories: readonly { id: string; name: string; ownerEmploymentId: string | null; ownerEnded: boolean }[];
  leadsInNoTerritory: number;
}

/** Coverage gaps: accounts in no territory, placed accounts a redraw never moves, and territories with no owner or an ended one (decision 0250). */
export function coverageReport(input: CoverageInput): { unassignedAccounts: { id: string; name: string }[]; placedAccounts: { id: string; name: string; territoryId: string }[]; unownedTerritories: { id: string; name: string; reason: 'NoOwner' | 'OwnerEnded' }[]; leadsInNoTerritory: number } {
  return {
    unassignedAccounts: input.companies.filter((c) => c.territoryId === null).map((c) => ({ id: c.id, name: c.name })),
    placedAccounts: input.companies.filter((c) => c.placed && c.territoryId !== null).map((c) => ({ id: c.id, name: c.name, territoryId: c.territoryId! })),
    unownedTerritories: input.territories.filter((t) => t.ownerEmploymentId === null || t.ownerEnded).map((t) => ({ id: t.id, name: t.name, reason: t.ownerEmploymentId === null ? ('NoOwner' as const) : ('OwnerEnded' as const) })),
    leadsInNoTerritory: input.leadsInNoTerritory,
  };
}

/** Realignment: every account whose assigned rep does not own the territory it now falls in, for a person to work through; nothing is reassigned. */
export function realignmentReport(rows: readonly { companyId: string; companyName: string; territoryId: string | null; territoryOwnerEmploymentId: string | null; assignedSalesRepEmploymentId: string | null }[]): { companyId: string; companyName: string; territoryId: string | null; territoryOwnerEmploymentId: string | null; assignedSalesRepEmploymentId: string | null }[] {
  return rows.filter((r) => r.assignedSalesRepEmploymentId !== null && r.territoryId !== null && r.territoryOwnerEmploymentId !== null && r.territoryOwnerEmploymentId !== r.assignedSalesRepEmploymentId);
}
